import type { MedplumClient } from '@medplum/core';
import type { Practitioner, Task } from '@medplum/fhirtypes';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  CLINICAL_TASK_EXTRACTION_JOB_SYSTEM,
  type ClinicalTaskEditExtras,
  clinicalTaskFindingExtrasSchema,
} from '@eluve/clinical-tasks';
import { useMedplum } from '@eluve/feature-patients';

import { useMiniAppApi } from '../../hooks';

import {
  CLINICAL_TASKS_AGENT_ID,
  CLINICAL_TASK_CATEGORY_SYSTEM,
  CLINICAL_TASK_ID_SYSTEM,
  DERIVED_FROM_APPOINTMENT_SYSTEM,
} from './constants';
import {
  type ClinicalTasksExtraction,
  getTasksPanelSnapshot,
  publishTasksPanel,
} from './tasks-panel-store';

/** A draft task awaiting review — served by the mini-app findings endpoint. */
export interface PendingClinicalTask {
  readonly id: string;
  readonly description: string;
  readonly category: string | null;
  readonly priority: string | null;
  readonly dueDate: string | null;
  readonly evidence: string | null;
  readonly confidence: number | null;
  readonly ownerReference: string | null;
  /** Patient display name, resolved server-side off Task.for. */
  readonly forDisplay: string | null;
}

/** An accepted task in its Todo/Done lifecycle — read from Medplum directly. */
export interface AcceptedClinicalTask {
  readonly id: string;
  readonly description: string;
  readonly dueDate: string | null;
  readonly ownerReference: string | null;
  readonly done: boolean;
  /** The full resource — the Done flip PUTs it back with the new status. */
  readonly raw: Task;
}

export interface PractitionerOption {
  readonly reference: string;
  readonly display: string;
}

export interface ClinicalTaskEdit {
  readonly description: string;
  readonly category: string;
  readonly priority: string | null;
  readonly dueDate: string | null;
  readonly ownerReference: string | null;
}

// MedplumClient caches searches in-memory (60s in the browser); every panel
// read must see writes that landed seconds ago, so read through the cache and
// invalidate Task searches after every mutation.
const NO_CACHE = { cache: 'no-cache' } as const;

// Extraction lands asynchronously after the visit completes, so the panel
// polls (bounded) until the extraction-job marker turns terminal — or, for
// marker-less visits (pre-marker data, Medplum client not ready), until the
// first task appears, mirroring the findings button's bounded poll. A live
// `generating` marker keeps the loop alive even with data showing, so a
// re-generation's superseded drafts refresh without a reload.
export const PANEL_POLL_INTERVAL_MS = 10_000;
export const PANEL_POLL_CAP_MS = 3 * 60_000;

const isClinicalTask = (task: Task): boolean =>
  Boolean(task.identifier?.some((i) => i.system === CLINICAL_TASK_ID_SYSTEM));

const practitionerDisplay = (p: Practitioner): string => {
  const name = p.name?.[0];
  const joined = [name?.given?.join(' '), name?.family]
    .filter(Boolean)
    .join(' ');
  return joined || name?.text || `Practitioner/${p.id}`;
};

/** Lifecycle of the extraction run off the job marker Task (the
 *  clinical-task-extraction-job identifier — care-plan-generation-job
 *  pattern). No marker / no client / read error all degrade to `unknown`,
 *  which renders exactly like the pre-marker behavior. */
async function fetchExtraction(
  client: MedplumClient | null,
  appointmentId: string,
): Promise<ClinicalTasksExtraction> {
  if (!client) return 'unknown';
  try {
    const marker = await client.searchOne(
      'Task',
      { identifier: `${CLINICAL_TASK_EXTRACTION_JOB_SYSTEM}|${appointmentId}` },
      NO_CACHE,
    );
    if (!marker) return 'unknown';
    if (marker.status === 'completed') return 'done';
    if (marker.status === 'failed' || marker.status === 'cancelled') {
      return 'failed';
    }
    return 'generating';
  } catch {
    return 'unknown';
  }
}

async function fetchAccepted(
  client: MedplumClient,
  appointmentId: string,
): Promise<AcceptedClinicalTask[]> {
  const found = await client.searchResources(
    'Task',
    {
      identifier: `${DERIVED_FROM_APPOINTMENT_SYSTEM}|${appointmentId}`,
      status: 'requested,completed',
      _count: 200,
    },
    NO_CACHE,
  );
  return found
    .filter(isClinicalTask)
    .filter((t): t is Task & { id: string } => Boolean(t.id))
    .map((t) => ({
      id: t.id,
      description: t.description ?? '(no description)',
      dueDate: t.restriction?.period?.end ?? null,
      ownerReference: t.owner?.reference ?? null,
      done: t.status === 'completed',
      raw: t,
    }))
    .sort((a, b) => a.description.localeCompare(b.description));
}

/**
 * All panel state + actions for the Clinical Tasks mini-app (ENG-5223).
 *
 * Reads split by trust: the pending (draft) list goes through the mini-app
 * findings endpoint (reviewer identity established server-side); the accepted
 * Todo/Done list and the Done flip go browser-direct under the clinician's own
 * Medplum token — matching the care-plan Accept pattern, so the audit trail
 * shows the clinician, not a service account.
 */
export function useClinicalTasksPanelState(args: {
  tenantId: string;
  appointmentId: string | undefined;
}) {
  const { tenantId, appointmentId } = args;
  const api = useMiniAppApi(tenantId, CLINICAL_TASKS_AGENT_ID);
  const { client } = useMedplum();

  const [pending, setPending] = useState<PendingClinicalTask[]>([]);
  const [accepted, setAccepted] = useState<AcceptedClinicalTask[]>([]);
  const [practitioners, setPractitioners] = useState<PractitionerOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState(false);

  const loadPending = useCallback(async () => {
    if (!appointmentId) return [] as PendingClinicalTask[];
    const res = await api.listFindings(appointmentId);
    if (res.status !== 200) return [] as PendingClinicalTask[];
    return res.body.findings.map((f) => {
      // The task-specific slice rides opaquely in `extras`; tolerate a missing
      // or malformed payload (degrade to nulls) rather than dropping the task.
      const parsed = clinicalTaskFindingExtrasSchema.safeParse(f.extras);
      const extras = parsed.success ? parsed.data : null;
      return {
        id: f.id,
        description: f.display,
        category: extras?.category ?? null,
        priority: extras?.priority ?? null,
        dueDate: extras?.dueDate ?? null,
        evidence: f.evidence,
        confidence: f.confidence,
        ownerReference: extras?.ownerReference ?? null,
        forDisplay: extras?.forDisplay ?? null,
      };
    });
  }, [api, appointmentId]);

  // Which appointment the lists currently reflect. Lets the poll effect skip
  // the `loading` flip when it re-fires for an appointment that is already
  // showing data (e.g. the Medplum client arriving late re-creates `refresh`)
  // — flipping to loading would blank the panel and unmount an open editor.
  const loadedForRef = useRef<string | null>(null);
  // True when the last refresh swallowed a fetch failure (auth still
  // settling, transient FHIR error). The poll must NOT stop on a degraded
  // round — a failed first fetch with a terminal marker would otherwise
  // freeze the panel empty until a reload.
  const degradedRef = useRef(false);

  /** Re-read both lists; resolves true once anything is showing. */
  const refresh = useCallback(async (): Promise<boolean> => {
    if (!appointmentId) {
      setPending([]);
      setAccepted([]);
      loadedForRef.current = null;
      setLoading(false);
      return false;
    }
    try {
      // Pending is served by the mini-app API; the accepted list and the
      // extraction-job marker need the browser Medplum client. The loads fail
      // independently so a broken or still-initializing Medplum login never
      // hides pending review work.
      let degraded = false;
      const [nextPending, nextAccepted, extraction] = await Promise.all([
        loadPending().catch(() => {
          degraded = true;
          return [] as PendingClinicalTask[];
        }),
        client
          ? fetchAccepted(client, appointmentId).catch(() => {
              degraded = true;
              return [] as AcceptedClinicalTask[];
            })
          : ([] as AcceptedClinicalTask[]),
        fetchExtraction(client, appointmentId),
      ]);
      degradedRef.current = degraded;
      setPending(nextPending);
      setAccepted(nextAccepted);
      // Single source for the Tasks action button (spinner/count) + collapse.
      publishTasksPanel(appointmentId, {
        pending: nextPending.length,
        accepted: nextAccepted.length,
        extraction,
      });
      return nextPending.length > 0 || nextAccepted.length > 0;
    } finally {
      loadedForRef.current = appointmentId;
      setLoading(false);
    }
  }, [appointmentId, client, loadPending]);

  useEffect(() => {
    let cancelled = false;
    // `tick` runs async, so `stop` is never invoked before the timers below
    // are initialized.
    const tick = async () => {
      const hasAny = await refresh();
      if (cancelled) {
        stop();
        return;
      }
      const { extraction } = getTasksPanelSnapshot();
      // Never stop on a degraded round — a swallowed fetch failure plus a
      // terminal marker must retry, not freeze the panel empty.
      const settled =
        !degradedRef.current &&
        (extraction === 'done' ||
          extraction === 'failed' ||
          (hasAny && extraction === 'unknown'));
      if (settled) stop();
    };
    // Only surface `loading` (which hides the panel) when this appointment's
    // data isn't showing yet — a re-poll of live data refreshes in place.
    if (loadedForRef.current !== (appointmentId ?? null)) setLoading(true);
    void tick();
    const interval = setInterval(() => void tick(), PANEL_POLL_INTERVAL_MS);
    const cap = setTimeout(() => clearInterval(interval), PANEL_POLL_CAP_MS);
    function stop() {
      clearInterval(interval);
      clearTimeout(cap);
    }
    return () => {
      cancelled = true;
      stop();
    };
  }, [refresh]);

  // Assignee options for the edit modal — Practitioner is tenant-member
  // readable, so this is a plain browser-direct read. Loaded once per client.
  useEffect(() => {
    if (!client) return;
    let cancelled = false;
    void (async () => {
      try {
        const found = await client.searchResources('Practitioner', {
          _count: 200,
        });
        if (cancelled) return;
        setPractitioners(
          found
            .filter((p): p is Practitioner & { id: string } => Boolean(p.id))
            .map((p) => ({
              reference: `Practitioner/${p.id}`,
              display: practitionerDisplay(p),
            }))
            .sort((a, b) => a.display.localeCompare(b.display)),
        );
      } catch {
        if (!cancelled) setPractitioners([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client]);

  const practitionerName = useCallback(
    (reference: string | null): string | null =>
      practitioners.find((p) => p.reference === reference)?.display ?? null,
    [practitioners],
  );

  /** Accept or reject one pending task through the review endpoint. */
  const decide = useCallback(
    async (id: string, decision: 'approve' | 'reject'): Promise<boolean> => {
      if (!appointmentId) return false;
      setMutating(true);
      try {
        const res = await api.review(appointmentId, [
          { resourceType: 'Task', id, decision },
        ]);
        if (res.status !== 200) return false;
        client?.invalidateSearches('Task');
        await refresh();
        return true;
      } catch {
        return false;
      } finally {
        setMutating(false);
      }
    },
    [api, appointmentId, client, refresh],
  );

  /** Apply an edit (description/category/priority/due date/assignee); stays
   *  pending. Optimistic: the pending card reflects the edit immediately so
   *  the preview tracks the edit sheet as the user types; a failed write
   *  re-syncs from the server. Deliberately does NOT toggle `mutating` —
   *  autosave fires on every settled change and must not flicker-disable the
   *  panel's buttons. */
  const applyEdit = useCallback(
    async (id: string, edit: ClinicalTaskEdit): Promise<boolean> => {
      if (!appointmentId) return false;
      setPending((prev) =>
        prev.map((t) =>
          t.id === id
            ? {
                ...t,
                description: edit.description,
                category: edit.category,
                priority: edit.priority,
                dueDate: edit.dueDate,
                ownerReference: edit.ownerReference,
              }
            : t,
        ),
      );
      try {
        const res = await api.editFinding({
          appointmentId,
          resourceType: 'Task',
          id,
          display: edit.description,
          system: CLINICAL_TASK_CATEGORY_SYSTEM,
          code: edit.category,
          extras: {
            priority: edit.priority,
            dueDate: edit.dueDate,
            ownerReference: edit.ownerReference,
          } satisfies ClinicalTaskEditExtras,
        });
        if (res.status !== 200) {
          await refresh();
          return false;
        }
        client?.invalidateSearches('Task');
        await refresh();
        return true;
      } catch {
        await refresh();
        return false;
      }
    },
    [api, appointmentId, client, refresh],
  );

  /** Flip Todo ↔ Done browser-direct under the clinician's own identity. */
  const setDone = useCallback(
    async (task: AcceptedClinicalTask, done: boolean): Promise<boolean> => {
      if (!client) return false;
      setMutating(true);
      try {
        await client.updateResource({
          ...task.raw,
          status: done ? 'completed' : 'requested',
        });
        client.invalidateSearches('Task');
        await refresh();
        return true;
      } catch {
        return false;
      } finally {
        setMutating(false);
      }
    },
    [client, refresh],
  );

  return useMemo(
    () => ({
      pending,
      accepted,
      practitioners,
      practitionerName,
      loading,
      mutating,
      refresh,
      decide,
      applyEdit,
      setDone,
    }),
    [
      pending,
      accepted,
      practitioners,
      practitionerName,
      loading,
      mutating,
      refresh,
      decide,
      applyEdit,
      setDone,
    ],
  );
}
